/**
 * Opportunity Stage Transition Engine
 * 
 * Defines strict rules, guardrails, and automation triggers for
 * moving opportunities through the sales lifecycle.
 */

import type { Opportunity, OpportunityStage, StageChange, OpportunityActivity, FollowUpType } from "./opportunityTypes";

/* ═══════ TRANSITION RULES ═══════ */

/** Which stages can a given stage transition to? */
export const ALLOWED_TRANSITIONS: Record<OpportunityStage, OpportunityStage[]> = {
  "Lead":                   ["Appointment Scheduled"],
  "Appointment Scheduled":  ["Appointment Completed"],
  "Appointment Completed":  ["Closed Won", "Follow-Up Needed"],
  "Follow-Up Needed":       ["Closed Won", "Closed Lost"],
  "Closed Won":             [],
  "Closed Lost":            ["Follow-Up Needed"],  // reopen
};

/** Human-readable labels for next expected actions */
export const NEXT_ACTION_LABELS: Record<OpportunityStage, string> = {
  "Lead":                   "Book an appointment to move forward",
  "Appointment Scheduled":  "Complete the consultation",
  "Appointment Completed":  "Close the deal or record follow-up",
  "Follow-Up Needed":       "Re-engage or close as lost",
  "Closed Won":             "Job created — proceed with operations",
  "Closed Lost":            "Reopen if customer returns",
};

/** Action button labels for each transition */
export const TRANSITION_LABELS: Partial<Record<string, string>> = {
  "Lead→Appointment Scheduled":            "Schedule Appointment",
  "Appointment Scheduled→Appointment Completed": "Complete Consultation",
  "Appointment Completed→Closed Won":      "Close as Won",
  "Appointment Completed→Follow-Up Needed": "Record Follow-Up",
  "Follow-Up Needed→Closed Won":           "Close as Won",
  "Follow-Up Needed→Closed Lost":          "Close as Lost",
  "Closed Lost→Follow-Up Needed":          "Reopen Opportunity",
};

/* ═══════ GUARDRAILS ═══════ */

export interface TransitionGuardrail {
  field: string;
  label: string;
  check: (opp: Opportunity) => boolean;
}

const GUARDRAILS: Partial<Record<string, TransitionGuardrail[]>> = {
  "Lead→Appointment Scheduled": [
    {
      field: "appointmentTime",
      label: "Appointment date/time must be set",
      check: (opp) => !!opp.appointmentTime,
    },
    {
      field: "assignedRep",
      label: "Sales rep must be assigned",
      check: (opp) => !!opp.assignedRep && opp.assignedRep !== "Unassigned",
    },
  ],
  "Appointment Scheduled→Appointment Completed": [
    {
      field: "projectConfig",
      label: "Consultation data must be saved",
      check: (opp) => !!opp.projectConfig && (opp.projectConfig.selectedVerticals?.length > 0 || !!opp.projectConfig.consultationCategory),
    },
  ],
  "Appointment Completed→Follow-Up Needed": [
    {
      field: "dispositionReason",
      label: "Disposition reason is required",
      check: (opp) => !!opp.dispositionReason?.trim(),
    },
    {
      field: "followUpType",
      label: "Follow-up type must be selected (Sales Rep or Manager)",
      check: (opp) => !!opp.followUpType && opp.followUpType !== "none",
    },
    {
      field: "followUpDueDate",
      label: "Follow-up due date is required",
      check: (opp) => !!opp.followUpDueDate,
    },
  ],
  "Appointment Completed→Closed Won": [
    {
      field: "contract",
      label: "Contract must be signed",
      check: (opp) => !!opp.contract && opp.contract.status === "Signed",
    },
  ],
  "Follow-Up Needed→Closed Won": [
    {
      field: "contract",
      label: "Contract must be signed",
      check: (opp) => !!opp.contract && opp.contract.status === "Signed",
    },
  ],
  "Follow-Up Needed→Closed Lost": [
    {
      field: "dispositionReason",
      label: "Disposition reason is required",
      check: (opp) => !!opp.dispositionReason?.trim(),
    },
  ],
  "Closed Lost→Follow-Up Needed": [
    {
      field: "followUpType",
      label: "Follow-up type must be selected",
      check: (opp) => !!opp.followUpType && opp.followUpType !== "none",
    },
    {
      field: "followUpDueDate",
      label: "Follow-up due date is required",
      check: (opp) => !!opp.followUpDueDate,
    },
  ],
};

/* ═══════ VALIDATION ═══════ */

export interface TransitionValidation {
  allowed: boolean;
  failedGuardrails: TransitionGuardrail[];
  transitionKey: string;
}

/**
 * Validate whether an opportunity can transition to a target stage.
 */
export function validateTransition(
  opp: Opportunity,
  targetStage: OpportunityStage,
): TransitionValidation {
  const transitionKey = `${opp.stage}→${targetStage}`;
  const allowed = ALLOWED_TRANSITIONS[opp.stage]?.includes(targetStage) ?? false;

  if (!allowed) {
    return { allowed: false, failedGuardrails: [], transitionKey };
  }

  const guardrails = GUARDRAILS[transitionKey] || [];
  const failed = guardrails.filter((g) => !g.check(opp));

  return {
    allowed: failed.length === 0,
    failedGuardrails: failed,
    transitionKey,
  };
}

/**
 * Get all valid transitions from current stage with their validation status.
 */
export function getAvailableTransitions(opp: Opportunity): Array<{
  targetStage: OpportunityStage;
  label: string;
  validation: TransitionValidation;
}> {
  const targets = ALLOWED_TRANSITIONS[opp.stage] || [];
  return targets.map((target) => {
    const key = `${opp.stage}→${target}`;
    return {
      targetStage: target,
      label: TRANSITION_LABELS[key] || `Move to ${target}`,
      validation: validateTransition(opp, target),
    };
  });
}

/* ═══════ STAGE CHANGE BUILDER ═══════ */

export function buildStageChange(
  fromStage: OpportunityStage | null,
  toStage: OpportunityStage,
  changedBy: string,
): StageChange {
  return {
    id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    fromStage,
    toStage,
    changedAt: new Date().toISOString(),
    changedBy,
  };
}

export function buildActivity(
  type: OpportunityActivity["type"],
  description: string,
  createdBy: string,
): OpportunityActivity {
  return {
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    type,
    description,
    createdAt: new Date().toISOString(),
    createdBy,
  };
}

/* ═══════ FOLLOW-UP DEFAULTS ═══════ */

/**
 * Calculate default follow-up due date (2 business days from now).
 */
export function getDefaultFollowUpDate(): string {
  const d = new Date();
  let added = 0;
  while (added < 2) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

/* ═══════ DISPOSITION REASONS ═══════ */

export const DISPOSITION_REASONS = [
  "Price too high",
  "Need to think about it",
  "Comparing competitors",
  "No decision maker present",
  "Timing not right",
  "Project postponed",
  "Financing concerns",
  "Went with competitor",
  "Not qualified",
  "Other",
] as const;

export type DispositionReasonLabel = typeof DISPOSITION_REASONS[number];

/* ═══════ APPOINTMENT TRACKING ═══════ */

export interface AppointmentRecord {
  id: string;
  scheduledAt: string;
  status: "scheduled" | "completed" | "no-show" | "rescheduled" | "cancelled";
  completedAt?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
}
